import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/PersonAddAlt1';
import EditIcon from '@mui/icons-material/Edit';
import apiService from '../../services/apiService';
import AddProductDialog from './AddProductDialog';

// Custom hook for debouncing
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

const headerCellSx = {
  '& .MuiTableCell-head': {
    backgroundColor: '#e3f2fd',
    fontWeight: 700,
    borderBottom: '1px solid #bbdefb'
  }
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchPlace, setSearchPlace] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editedProducts, setEditedProducts] = useState([]);
  const originalProductsRef = useRef([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const debouncedSearch = useDebounce(search, 300);
  const debouncedSearchPlace = useDebounce(searchPlace, 300);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        size: rowsPerPage,
        sortBy: 'productId',
        direction: 'ASC',
      };
      if (debouncedSearch.length >= 2) {
        params.name = debouncedSearch;
      }
      if (debouncedSearchPlace.length >= 2) {
        params.place = debouncedSearchPlace;
      }
      const resp = await apiService.products.list(params);
      const data = resp.data;
      const content = data?.content || [];
      setProducts(Array.isArray(content) ? content : []);
      setTotalCount(data?.totalElements ?? 0);
    } catch (e) {
      console.error('Failed to load products', e);
      setProducts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, debouncedSearchPlace]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const openAdd = () => {
    setSelectedProduct(null);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setSelectedProduct(row);
    setDialogOpen(true);
  };
  const closeDialog = () => setDialogOpen(false);

  const handleSaveSuccess = async () => {
    await loadProducts();
  };

  const handleEditModeToggle = () => {
    if (editMode) {
      setEditedProducts([]);
      loadProducts(); // Refresh data when exiting edit mode
    } else {
      originalProductsRef.current = JSON.parse(JSON.stringify(products));
      setEditedProducts(JSON.parse(JSON.stringify(products)));
    }
    setEditMode(!editMode);
  };

  const handleBulkEditChange = (productId, field, value) => {
    setEditedProducts(prev =>
      prev.map(p => (p.productId === productId ? { ...p, [field]: value } : p))
    );
  };

  const handleAutoSave = async (productId) => {
    const originalProduct = originalProductsRef.current.find(p => p.productId === productId);
    const editedProduct = editedProducts.find(p => p.productId === productId);

    if (!editedProduct || !originalProduct) return;

    // Check if any value has actually changed
    if (
      String(originalProduct.actualPrice) !== String(editedProduct.actualPrice) ||
      String(originalProduct.sellingPrice) !== String(editedProduct.sellingPrice) ||
      String(originalProduct.stock) !== String(editedProduct.stock)
    ) {
      try {
        const payload = {
          ...editedProduct,
          actualPrice: editedProduct.actualPrice === '' ? null : parseFloat(editedProduct.actualPrice),
          sellingPrice: editedProduct.sellingPrice === '' ? null : parseFloat(editedProduct.sellingPrice),
          stock: editedProduct.stock === '' ? null : parseFloat(editedProduct.stock),
        };
        await apiService.products.update(payload);
        // Update the original product reference to prevent re-saving unchanged data
        originalProductsRef.current = originalProductsRef.current.map(p =>
          p.productId === productId ? JSON.parse(JSON.stringify(editedProduct)) : p
        );
      } catch (error) {
        console.error(`Failed to auto-save product ${productId}`, error);
        // Optionally, show a small error toast to the user
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 24px)' }}>
      <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 1.5 }}>
        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Products</Typography>
              {!editMode && (
                <Button variant="contained" onClick={openAdd} sx={{ minWidth: 'auto', px: 1.5 }}>
                  <AddIcon />
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Name"
                size="small"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                fullWidth
              />
              <TextField
                label="Place"
                size="small"
                value={searchPlace}
                onChange={(e) => { setSearchPlace(e.target.value); setPage(0); }}
                fullWidth
              />
            </Box>
            <Button 
              variant={editMode ? "contained" : "outlined"} 
              color={editMode ? "success" : "primary"} 
              onClick={handleEditModeToggle} 
              fullWidth
              startIcon={!editMode && <EditIcon />}
            >
              {editMode ? "Done" : "Edit Prices & Stock"}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Products</Typography>
                {!editMode && (
                  <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} size="small" sx={{ textTransform: 'none' }}>
                    Add Product
                  </Button>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">Search, add, and edit products</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                label="Search Name"
                size="small"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                sx={{ width: 200 }}
              />
              <TextField
                label="Search Place"
                size="small"
                value={searchPlace}
                onChange={(e) => { setSearchPlace(e.target.value); setPage(0); }}
                sx={{ width: 200 }}
              />
              <Button 
                variant={editMode ? "contained" : "outlined"} 
                color={editMode ? "success" : "primary"} 
                onClick={handleEditModeToggle}
                startIcon={!editMode && <EditIcon />}
              >
                {editMode ? "Done" : "Edit Prices"}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pb: '70px' }}>
        {isMobile ? (
          <Box sx={{ pb: 1 }}>
            {(editMode ? editedProducts : products).map((row) => (
              <Paper key={row.productId} sx={{ p: 1.5, mb: 1, mx: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {row.productName}
                      </Typography>
                      {(row.productNameTamil || row.productNameEnglish) && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {row.productNameTamil && (
                              <Typography variant="caption" color="text.secondary">
                                {row.productNameTamil}
                              </Typography>
                            )}
                            {row.productNameEnglish && (
                              <Typography variant="caption" color="text.secondary">
                                {row.productNameEnglish}
                              </Typography>
                            )}
                        </Box>
                      )}
                    </Box>
                    {!editMode && (
                      <IconButton size="small" onClick={() => openEdit(row)} sx={{ p: 0.5, ml: 1 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                    <Box>
                      <Typography variant="caption" display="block" color="text.secondary">Actual</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          type="number" 
                          value={row.actualPrice} 
                          onChange={(e) => handleBulkEditChange(row.productId, 'actualPrice', e.target.value)}
                          onBlur={() => handleAutoSave(row.productId)}
                          sx={{ bgcolor: 'white' }}
                          inputProps={{ style: { padding: '4px 8px', fontSize: '0.875rem' } }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.actualPrice}</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" display="block" color="text.secondary">Selling</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          type="number" 
                          value={row.sellingPrice} 
                          onChange={(e) => handleBulkEditChange(row.productId, 'sellingPrice', e.target.value)}
                          onBlur={() => handleAutoSave(row.productId)}
                          sx={{ bgcolor: 'white' }}
                          inputProps={{ style: { padding: '4px 8px', fontSize: '0.875rem' } }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.sellingPrice}</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" display="block" color="text.secondary">Stock</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          type="number" 
                          value={row.stock} 
                          onChange={(e) => handleBulkEditChange(row.productId, 'stock', e.target.value)}
                          onBlur={() => handleAutoSave(row.productId)}
                          sx={{ bgcolor: 'white' }}
                          inputProps={{ style: { padding: '4px 8px', fontSize: '0.875rem' } }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.stock}</Typography>
                      )}
                    </Box>
                  </Box>
              </Paper>
            ))}
            {!loading && products.length === 0 && (
              <Typography variant="body2" align="center" sx={{ mt: 4, color: 'text.secondary' }}>No products found</Typography>
            )}
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: '100%' }}>
          <Table stickyHeader size="small">
            <TableHead sx={headerCellSx}>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Name (Tamil)</TableCell>
                <TableCell>Name (English)</TableCell> {/* New Column */}
                <TableCell>Place</TableCell>
                <TableCell>Actual Price</TableCell>
                <TableCell>Selling Price</TableCell>
                <TableCell>Stock</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(editMode ? editedProducts : products).map((row) => (
                <TableRow key={row.productId} hover>
                  <TableCell>{row.productId}</TableCell>
                  <TableCell>{row.productName}</TableCell>
                  <TableCell>{row.productNameTamil}</TableCell>
                  <TableCell>{row.productNameEnglish}</TableCell> {/* New Column Data */}
                  <TableCell>{row.productPlace}</TableCell>
                  <TableCell>
                    {editMode ? (
                      <TextField
                        size="small"
                        type="number"
                        value={row.actualPrice}
                        onChange={(e) => handleBulkEditChange(row.productId, 'actualPrice', e.target.value)}
                        onBlur={() => handleAutoSave(row.productId)}
                        sx={{ width: 100 }}
                      />
                    ) : (
                      row.actualPrice
                    )}
                  </TableCell>
                  <TableCell>
                    {editMode ? (
                      <TextField
                        size="small"
                        type="number"
                        value={row.sellingPrice}
                        onChange={(e) => handleBulkEditChange(row.productId, 'sellingPrice', e.target.value)}
                        onBlur={() => handleAutoSave(row.productId)}
                        sx={{ width: 100 }}
                      />
                    ) : (
                      row.sellingPrice
                    )}
                  </TableCell>
                  <TableCell>
                    {editMode ? (
                      <TextField
                        size="small"
                        type="number"
                        value={row.stock}
                        onChange={(e) => handleBulkEditChange(row.productId, 'stock', e.target.value)}
                        onBlur={() => handleAutoSave(row.productId)}
                        sx={{ width: 100 }}
                      />
                    ) : (
                      row.stock
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEdit(row)} disabled={editMode}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && products.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center"> {/* Updated colspan */}
                    <Typography variant="body2" color="text.secondary">No products found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </TableContainer>
        )}
      </Box>

      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }} elevation={3}>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      <AddProductDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSaveSuccess={handleSaveSuccess}
        initialData={selectedProduct}
      />
    </Box>
  );
};

export default Products;
